import { useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import { ORGANIZATIONS_SEARCH_KEY, SEARCH_DATA_KEY } from 'api/search'
import axios from 'axios'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { Organization } from 'types'

const createOrganization = async (orgData: Partial<Organization>): Promise<Organization> => {
  const url = `${baseUrl}/aws/organizations_table/organization`
  console.log('New Organization Data: ', orgData)
  const response = await axios.post(url, orgData)
  return response.data // Assumes the response data includes the new contact with `id`
}

export const usePostOrganization = (queryKey = ['organization']) => {
  const { showSnackbar } = useSnackbarContext()
  const queryClient = useQueryClient()
  const mutation = useMutation<Organization, Error, Partial<Organization>, { previousOrgs: Organization[] | undefined }>({
    mutationFn: createOrganization,
    // No optimistic insert. The server mints PK / SK / org_uuid, so any row we
    // add before the response has no key to route on — it rendered in lists as
    // an org whose link resolved to /organizations/undefined/details. The POST
    // returns the real record in well under a second; seeding the caches from
    // that (below) is both correct and fast enough.
    onMutate: async (): Promise<{ previousOrgs: Organization[] | undefined }> => {
      await queryClient.cancelQueries({ queryKey: ['organization'] })
      const previousOrgs = queryClient.getQueryData<Organization[]>(['organization'])
      return { previousOrgs }
    },
    onError: (error, _newOrg, context) => {
      if (context?.previousOrgs) {
        queryClient.setQueryData(['organization'], context.previousOrgs)
      }
      console.error('[usePostOrganization] Mutation Error:', error)
      showSnackbar((error as any).response?.data?.message || error.message, 'error')
    },
    onSuccess: data => {
      console.log('[usePostOrganization] Org Created Successfully:', data)

      queryClient.setQueryData<Organization[]>(['organization'], oldOrgs =>
        oldOrgs ? [...oldOrgs.filter(o => o?.PK !== data?.PK), data] : [data]
      )

      // Seed the cache the rest of the app actually reads. Without this the new
      // org is only reachable once /search/minimal is refetched or the backend
      // index picks it up, so anything that resolves an org by PK or org_uuid
      // (the detail page, parent/child links, autocompletes) came back empty
      // right after creation.
      if (data?.PK) {
        queryClient.setQueryData<any>(SEARCH_DATA_KEY, old => {
          if (!old) return old
          const organizations = Array.isArray(old.organizations) ? old.organizations : []
          if (organizations.some((o: any) => o?.PK === data.PK)) return old
          return { ...old, organizations: [...organizations, data] }
        })
      }

      showSnackbar('Org created successfully', 'success')
    },
    onSettled: (data, error) => {
      console.log('data: ', data, 'error: ', error)
      queryClient.invalidateQueries({ queryKey: queryKey })
      // Invalidate search cache when organization is created
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_SEARCH_KEY })
    },
  })

  return mutation
}
